import { useRef, Fragment } from 'react';
import { motion, useInView } from 'framer-motion';
import { Check, X, TrendingUp, BarChart3, ShieldCheck, ArrowRight } from 'lucide-react';

// Kolumny konkurencji (DAIEN zawsze ma wszystko na tak)
const competitors = ['Agencja marketingowa', 'Freelancer', 'Software house', 'Zrób to sam'];

// Wiersze porownania. others = [agencja, freelancer, software house, DIY]
const rows = [
  { label: 'Działające systemy AI, nie tylko strategia i raporty', others: [false, true, true, false] },
  { label: 'Wdrożenie w 2-4 tygodnie', others: [false, true, false, false] },
  { label: 'Działa 24/7 bez Twojej obsługi', others: [false, false, true, false] },
  { label: 'Dane i system zostają u Ciebie, bez vendor lock-in', others: [false, false, false, true] },
  { label: 'Długoterminowe partnerstwo, nie znika po projekcie', others: [false, false, true, false] },
  { label: 'Mierzalny ROI: koszt leada i koszt spotkania', others: [true, false, true, false] },
];

const pillars = [
  { icon: TrendingUp, title: 'Skaluje Twoj biznes', description: 'Systemy, ktore rosna razem z firma, bez proporcjonalnego zatrudniania.' },
  { icon: BarChart3, title: 'Zwiekszam ruch i leady', description: 'Lead gen, reklamy z AI i outbound, ktore realnie dowoza klientow.' },
  { icon: ShieldCheck, title: 'Buduje zaufanie', description: 'Nie promuje firm ze slabymi opiniami. Tylko uczciwi klienci, tylko dlugoterminowo.' },
];

function Mark({ ok }: { ok: boolean }) {
  return ok
    ? <Check className="w-5 h-5 text-accent-to mx-auto" aria-label="tak" />
    : <X className="w-5 h-5 text-muted/40 mx-auto" aria-label="nie" />;
}

const ctaBtn =
  'btn-animated inline-flex items-center justify-center gap-2 bg-text-primary text-bg rounded-full px-6 py-3 text-sm font-medium';

export default function WhyNotAgency() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section aria-label="Dlaczego DAIEN, a nie agencja" className="py-16 md:py-24 bg-bg">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16">

        {/* Header */}
        <motion.div
          className="mb-12 md:mb-16 max-w-2xl"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 1, ease: 'easeOut' }}
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-8 h-px bg-stroke"></div>
            <span className="text-xs text-muted uppercase tracking-[0.3em]">Dlaczego DAIEN, a nie agencja</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-text-primary mb-5">
            Nie jestem kolejna <span className="font-display italic bg-gradient-to-r from-[hsl(var(--accent-from))] via-[hsl(var(--accent-to))] to-text-primary bg-clip-text text-transparent pr-1">agencja marketingowa.</span>
          </h2>
          <p className="text-lg text-muted leading-relaxed">
            Nie sprzedaje godzin na ekran ani raportow. Buduje systemy, ktore realnie skaluja biznes, ruch i zaufanie.
          </p>
        </motion.div>

        {/* MOBILE: karta DAIEN (same plusy + CTA) */}
        <div className="md:hidden bg-surface border border-accent-to/40 rounded-3xl p-7 mb-10">
          <div className="flex items-center gap-2 mb-6">
            <img src="/logoDaien.svg" alt="DAIEN" className="w-7 h-7" width="28" height="28" />
            <span className="text-lg font-semibold text-text-primary">DAIEN</span>
          </div>
          <ul className="space-y-3 mb-7">
            {rows.map((r) => (
              <li key={r.label} className="flex items-start gap-3 text-text-primary">
                <Check className="w-5 h-5 shrink-0 mt-0.5 text-accent-to" />
                <span className="leading-snug">{r.label}</span>
              </li>
            ))}
          </ul>
          <a href="#kontakt" className={`${ctaBtn} w-full`}>
            Umów rozmowę
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {/* DESKTOP: macierz porownania, DAIEN jako wyrozniona kolumna */}
        <motion.div
          ref={ref}
          className="hidden md:block mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        >
          <div className="grid grid-cols-[1.6fr_repeat(5,minmax(0,1fr))] items-stretch">
            {/* Naglowek */}
            <div aria-hidden="true" />
            <div className="flex items-center justify-center gap-2 bg-surface border-x border-t border-accent-to/40 rounded-t-2xl px-3 pt-5 pb-3">
              <img src="/logoDaien.svg" alt="DAIEN" className="w-6 h-6" width="24" height="24" />
              <span className="font-semibold text-text-primary">DAIEN</span>
            </div>
            {competitors.map((c) => (
              <div key={c} className="flex items-end justify-center text-center px-2 pb-3">
                <span className="text-sm font-medium text-muted leading-tight">{c}</span>
              </div>
            ))}

            {/* Wiersze */}
            {rows.map((r, ri) => {
              const last = ri === rows.length - 1;
              return (
                <Fragment key={r.label}>
                  <div className="flex items-center text-sm md:text-base text-text-primary border-t border-stroke/60 py-4 pr-4">
                    {r.label}
                  </div>
                  <div className={`flex items-center justify-center bg-surface border-x border-accent-to/40 py-4 ${last ? '' : ''}`}>
                    <Check className="w-5 h-5 text-accent-to mx-auto" aria-label="tak" />
                  </div>
                  {r.others.map((ok, ci) => (
                    <div key={ci} className="flex items-center justify-center border-t border-stroke/60 py-4">
                      <Mark ok={ok} />
                    </div>
                  ))}
                </Fragment>
              );
            })}

            {/* Stopka z CTA pod kolumna DAIEN */}
            <div aria-hidden="true" />
            <div className="flex items-center justify-center bg-surface border-x border-b border-accent-to/40 rounded-b-2xl px-3 pt-3 pb-5">
              <a href="#kontakt" className={ctaBtn}>
                Umów rozmowę
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="col-span-4" aria-hidden="true" />
          </div>
        </motion.div>

        {/* 3 filary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {pillars.map((pillar, idx) => {
            const Icon = pillar.icon;
            return (
              <motion.div
                key={pillar.title}
                className="bg-surface border border-stroke rounded-2xl p-6"
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.6, delay: 0.15 + 0.1 * idx, ease: 'easeOut' }}
              >
                <div className="w-12 h-12 rounded-xl bg-bg border border-stroke flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-accent-to" />
                </div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">{pillar.title}</h3>
                <p className="text-sm text-muted leading-relaxed">{pillar.description}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Zdanie domykajace */}
        <motion.p
          className="max-w-3xl mx-auto text-center text-base md:text-lg text-muted leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
        >
          Nie promuje firm, ktore maja slabe opinie albo naciagaja klientow. To nie jest moj styl pracy.
          Buduje tylko dlugoterminowe wspolprace z ludzmi, ktorym moge zaufac.
        </motion.p>

      </div>
    </section>
  );
}
